import axios from 'axios';

/**
 * Axios instance. Auth uses httpOnly cookies (access + refresh), so
 * `withCredentials` is required. On a 401 we transparently attempt one
 * refresh, then replay the original request.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

let isRefreshing = false;
let queue = [];

const flushQueue = (error) => {
  queue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve()));
  queue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error;
    if (!response) return Promise.reject(error);

    // Normalize a friendly flag for 429 rate limits (free-tier APIs).
    if (response.status === 429) {
      error.isRateLimit = true;
    }

    // Attempt a single silent refresh on 401 (but never for auth endpoints).
    const isAuthRoute = config?.url?.includes('/auth/');
    if (response.status === 401 && !config._retry && !isAuthRoute) {
      if (isRefreshing) {
        // Queue until the in-flight refresh resolves.
        return new Promise((resolve, reject) => {
          queue.push({
            resolve: () => resolve(api({ ...config, _retry: true })),
            reject,
          });
        });
      }
      config._retry = true;
      isRefreshing = true;
      try {
        await api.post('/auth/refresh');
        flushQueue(null);
        return api(config);
      } catch (refreshErr) {
        flushQueue(refreshErr);
        // Broadcast so AuthContext can clear state + redirect.
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/** Extract a human-friendly message from an axios error. */
export function getErrorMessage(error, fallback = 'Something went wrong.') {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

export default api;
